import { Box, Checkbox } from "@mui/material";
import {
  CellValueChangedEvent,
  ColDef,
  GridApi,
  IHeaderParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";

import { RfpCoverageResponse } from "../../api/rfp";

const CHECKBOX_COLOR = "#2196f3";

// Column width constants
const CHECKBOX_COLUMN_WIDTH = 50;
const CLASS_NAME_COLUMN_WIDTH = 200;
const COVERAGE_COLUMN_WIDTH = 150;

export interface BenefitsClassRow {
  id: string;
  className: string;
  dental: boolean;
  vision: boolean;
  std: boolean;
  ltd: boolean;
  voluntaryLife: boolean;
  eap: boolean;
  hi: boolean;
  basicLife: boolean;
  ci: boolean;
  accident: boolean;
  cancer: boolean;
  idi: boolean;
  [key: string]: string | boolean; // Allow dynamic LOC properties
}

interface BenefitsClassGridProps {
  rowData: BenefitsClassRow[];
  coverages: RfpCoverageResponse[];
  classesWithoutCoverages: string[];
  onSelectionChange?: (selectedRows: BenefitsClassRow[]) => void;
  onGridReady?: (api: GridApi) => void;
  onCellValueChanged?: (
    event?: CellValueChangedEvent<BenefitsClassRow>,
  ) => void;
  onHeaderCheckboxClick?: (field: string, newValue: boolean) => void;
}

/**
 * Maps backend coverage types to grid field names
 * This ensures consistency with the COVERAGE_TYPE_MAP in transforms.ts
 */
const COVERAGE_TYPE_TO_FIELD: Record<string, string> = {
  dental: "dental",
  vision: "vision",
  std: "std",
  ltd: "ltd",
  voluntaryLifeAdnd: "voluntaryLife",
  eap: "eap",
  hospitalIndemnity: "hi",
  basicLifeAdnd: "basicLife",
  criticalIllness: "ci",
  accident: "accident",
  cancer: "cancer",
  individualDisabilityInsurance: "idi",
};

/**
 * Human-readable labels for coverage types
 * Provides custom labels where needed, otherwise formats the type name
 */
const COVERAGE_LABELS: Record<string, string> = {
  dental: "Dental",
  vision: "Vision",
  std: "STD",
  ltd: "LTD",
  voluntaryLifeAdnd: "Voluntary Life",
  eap: "EAP",
  hospitalIndemnity: "HI",
  basicLifeAdnd: "Basic Life",
  criticalIllness: "CI",
  accident: "Accident",
  cancer: "Cancer",
  individualDisabilityInsurance: "IDI",
  wholeLife: "Whole Life",
  fmla: "FMLA",
};

/**
 * Gets human-readable label for a coverage type
 * Falls back to formatting the type name if no custom label exists
 */
const getCoverageLabel = (type: string): string =>
  COVERAGE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1);

/**
 * Custom header component with checkbox for coverage columns
 */
interface CoverageHeaderParams extends IHeaderParams {
  field: string;
  onHeaderCheckboxClick: (field: string, newValue: boolean) => void;
}

interface CheckboxState {
  checked: boolean;
  indeterminate: boolean;
}

const CoverageHeaderComponent = (
  params: CoverageHeaderParams,
): React.JSX.Element => {
  const { displayName, field, api, onHeaderCheckboxClick } = params;

  // Calculate checkbox state based on all row values
  const getCheckboxState = (): CheckboxState => {
    const allRows: BenefitsClassRow[] = [];
    api.forEachNode((node) => {
      if (node.data) {
        allRows.push(node.data);
      }
    });

    if (allRows.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const checkedCount = allRows.filter((row) => row[field] === true).length;

    if (checkedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (checkedCount === allRows.length) {
      return { checked: true, indeterminate: false };
    }
    return { checked: false, indeterminate: true };
  };

  const { checked, indeterminate } = getCheckboxState();

  const handleClick = (): void => {
    // From indeterminate or unchecked -> check all
    // From checked -> uncheck all
    const newValue = !checked;
    onHeaderCheckboxClick(field, newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: "100%",
        cursor: "pointer",
      }}
      onClick={handleClick}
    >
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        size="small"
        sx={{
          p: 0,
          ml: "-2px",
          color: CHECKBOX_COLOR,
          "&.Mui-checked": {
            color: CHECKBOX_COLOR,
          },
          "&.MuiCheckbox-indeterminate": {
            color: CHECKBOX_COLOR,
          },
        }}
      />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {displayName}
      </span>
    </Box>
  );
};

const BenefitsClassGrid = ({
  rowData,
  coverages,
  classesWithoutCoverages,
  onSelectionChange,
  onGridReady,
  onCellValueChanged,
  onHeaderCheckboxClick,
}: BenefitsClassGridProps): React.JSX.Element => {
  const columnDefs = useMemo<ColDef<BenefitsClassRow>[]>(
    () => [
      {
        field: "className",
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: CHECKBOX_COLUMN_WIDTH,
        pinned: "left",
        lockPosition: true,
      },
      {
        headerName: "Class",
        field: "className",
        width: CLASS_NAME_COLUMN_WIDTH,
        pinned: "left",
        editable: true,
        cellStyle: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        tooltipField: "className",
        cellEditor: "agTextCellEditor",
        cellEditorParams: {
          placeholder: "Enter Class Name",
        },
        enableRowGroup: true,
      },
      ...coverages.map((coverage) => {
        const field = COVERAGE_TYPE_TO_FIELD[coverage.type] || coverage.type;
        const label = getCoverageLabel(coverage.type);

        return {
          headerName: label,
          field,
          width: COVERAGE_COLUMN_WIDTH,
          cellRenderer: "agCheckboxCellRenderer",
          cellEditor: "agCheckboxCellEditor",
          editable: true,
          enableRowGroup: true,
          headerComponent: CoverageHeaderComponent,
          headerComponentParams: {
            field,
            onHeaderCheckboxClick,
          },
        };
      }),
    ],
    [coverages, onHeaderCheckboxClick],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
    }),
    [],
  );

  // Calculate minimum width based on fixed columns + dynamic coverage columns
  const minGridWidth = useMemo(() => {
    const fixedColumnsWidth = CHECKBOX_COLUMN_WIDTH + CLASS_NAME_COLUMN_WIDTH;
    const coverageColumnsWidth = coverages.length * COVERAGE_COLUMN_WIDTH;
    return fixedColumnsWidth + coverageColumnsWidth;
  }, [coverages.length]);

  return (
    <Box
      className="ag-theme-quartz"
      sx={{
        height: "100%",
        width: "100%",
        minWidth: minGridWidth,
        "& .invalid-class-row": {
          backgroundColor: "error.lighter",
          "& .ag-cell": {
            color: "error.dark",
          },
        },
        "& .ag-center-cols-container": {
          width: "100% !important",
        },
      }}
    >
      <AgGridReact<BenefitsClassRow>
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        enableBrowserTooltips={true}
        rowSelection="multiple"
        animateRows={true}
        stopEditingWhenCellsLoseFocus={true}
        suppressMovableColumns={true}
        rowGroupPanelShow="always"
        suppressGroupChangesColumnVisibility={true}
        suppressDragLeaveHidesColumns={true}
        sideBar={{
          toolPanels: [
            {
              id: "columns",
              labelDefault: "Columns",
              labelKey: "columns",
              iconKey: "columns",
              toolPanel: "agColumnsToolPanel",
              toolPanelParams: {
                suppressRowGroups: false,
                suppressValues: true,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressColumnFilter: false,
                suppressColumnSelectAll: false,
                suppressColumnExpandAll: false,
              },
            },
          ],
          defaultToolPanel: "",
        }}
        rowClassRules={{
          "invalid-class-row": (params) =>
            classesWithoutCoverages.includes(params.data?.id || ""),
        }}
        onGridReady={(params) => onGridReady?.(params.api)}
        onSelectionChanged={(event) => {
          const selectedRows = event.api.getSelectedRows();
          onSelectionChange?.(selectedRows);
        }}
        onCellValueChanged={(event) => onCellValueChanged?.(event)}
      />
    </Box>
  );
};

export default BenefitsClassGrid;
